/**
 * AdminDoubleShoot — A/B comparison: our app's photos vs the competitor's same-day photos.
 *
 * For the 3-photographer double-shoot test. Auto-detects cars shot with our app,
 * and compares ONLY the fresh competitor batch (photos that arrived on/after our shoot
 * date) — pre-existing feed photos are excluded (they may be the dealer's own / a
 * prior vendor, not the same-day competitor shoot). Live: re-pulls each load, so a car
 * flips from "awaiting" to "ready" once the next feed delivers the competitor's set.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';

export default function AdminDoubleShoot() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [readyOnly, setReadyOnly] = useState(false);

  const authHeader = useCallback(() => {
    const t = localStorage.getItem('adminAuth');
    if (!t) { navigate('/admin/login'); return null; }
    return { Authorization: `Bearer ${t}` };
  }, [navigate]);

  const load = useCallback(() => {
    const h = authHeader();
    if (!h) return;
    setLoading(true);
    fetch(`${API_BASE}/admin/double-shoot-comparison`, { headers: h })
      .then(r => { if (r.status === 401 || r.status === 403) { localStorage.removeItem('adminAuth'); navigate('/admin/login'); throw new Error('expired'); } return r.json(); })
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authHeader, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;
  if (!data) return <div style={{ padding: 24 }}>Could not load.</div>;

  const cars = (data.cars || []).filter(c => !readyOnly || c.status === 'ready');

  const Thumbs = ({ urls }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {urls.length === 0 && <span style={{ color: '#999', fontSize: 13 }}>— none —</span>}
      {urls.map((u, i) => (
        <a key={i} href={u} target="_blank" rel="noreferrer">
          <img src={u} alt="" loading="lazy"
               style={{ width: 76, height: 57, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }} />
        </a>
      ))}
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ margin: '0 0 4px' }}>Double-Shoot Comparison — Our App vs Competitor</h1>
      <p style={{ color: '#666', marginTop: 0 }}>
        Compares <b>our app's photos</b> against the <b>competitor photos that arrived on/after our shoot</b> for the same car.
        Pre-existing feed photos are excluded (may not be the competitor's). Auto-updates as new feeds land.
      </p>
      <div style={{ margin: '10px 0', display: 'flex', gap: 16, alignItems: 'center' }}>
        <b>{data.total}</b> double-shoot cars ·
        <span style={{ color: '#0a7', fontWeight: 700 }}>{data.ready} ready</span> ·
        <span style={{ color: '#888' }}>{data.awaiting} awaiting competitor feed</span>
        <label style={{ marginLeft: 12, fontSize: 14 }}>
          <input type="checkbox" checked={readyOnly} onChange={e => setReadyOnly(e.target.checked)} /> ready only
        </label>
        <button onClick={load} style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: 6, border: '1px solid #ccc', cursor: 'pointer' }}>↻ Refresh</button>
      </div>

      {cars.map(c => (
        <div key={`${c.dealership_id}-${c.vin}`} style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, padding: 14, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <b>{c.stock || c.vin}</b>
            <span style={{ color: '#555' }}>{c.vehicle}</span>
            <span style={{ color: '#999', fontSize: 13 }}>{c.dealership_id} · {c.vin}</span>
            <span style={{ marginLeft: 'auto', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700,
                           background: c.status === 'ready' ? '#e6f7ee' : '#f0f0f0',
                           color: c.status === 'ready' ? '#0a7' : '#888' }}>
              {c.status === 'ready' ? 'READY' : 'AWAITING COMPETITOR FEED'}
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 6, color: '#007AFF' }}>
                Our App — {c.app_count} photos <span style={{ color: '#999', fontWeight: 400 }}>(shot {c.app_date})</span>
              </div>
              <Thumbs urls={c.app_photos} />
            </div>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 6, color: '#C77700' }}>
                Competitor (same-day) — {c.competitor_fresh_count} photos
                <span style={{ color: '#999', fontWeight: 400 }}>{c.competitor_fresh_dates.length ? ` (arrived ${c.competitor_fresh_dates.join(', ')})` : ''}</span>
              </div>
              <Thumbs urls={c.competitor_photos} />
              {c.competitor_old_excluded > 0 && (
                <div style={{ color: '#aaa', fontSize: 12, marginTop: 4 }}>{c.competitor_old_excluded} pre-existing feed photo(s) excluded</div>
              )}
            </div>
          </div>
        </div>
      ))}
      {cars.length === 0 && <div style={{ color: '#999' }}>No cars to show.</div>}
    </div>
  );
}
